import { type Address } from "@finway-group/shared/lib/models/company/address.interface"
import { AutoComplete, Form, type FormItemProps, Input } from "antd"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDebouncedCallback } from "use-debounce/lib"
import { NoSearchDataFound } from "~Components/NoSearchDataFound"
import { DEBOUNCE_DURATION_LONG, DEBOUNCE_DURATION_MEDIUM } from "~Shared/config/consts"
import { type LocationAddress } from "~Shared/services/geoLocation.service"
import { formatAddress } from "~Shared/utils/form.utils"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { useLocationQuery } from "../queries/geolocation.queries"

interface PurchaseOrderAddressAutocompleteProps extends Omit<FormItemProps, "children"> {
    placeholder: string
    onSelect: (e: Address) => void
    className?: string
    label?: string
    name?: string | Array<string> | number
    formItemKey?: string | number
}

interface LocationForForm {
    label: React.ReactElement | string
    value: string
    disabled?: boolean
    address?: Address
}

const locAddToLocationForForm = (locAdd: LocationAddress): LocationForForm => ({
    label: locAdd.address,
    value: locAdd.address,
    address: {
        addressLine1: locAdd.street || "",
        addressLine2: "",
        city: locAdd.city || "",
        country: locAdd.country || "",
        zip: locAdd.zipCode || "",
    },
})

export const PurchaseOrderAddressAutocomplete = ({
    placeholder,
    onSelect,
    className,
    label,
    name,
    formItemKey,
    initialValue,
    rules,
    ...rest
}: PurchaseOrderAddressAutocompleteProps) => {
    const { t } = useTranslation()
    const [searchText, setSearchText] = useState<string>("")

    const isMountedRef = useRef(true)

    // Use the location query with proper parameters
    const { data, isLoading, error } = useLocationQuery(searchText, !!searchText?.trim())
    // Memoize search results to prevent unnecessary re-renders
    const memoizedOptions: Array<LocationForForm> = useMemo(() => {
        if (isLoading) {
            return [
                {
                    value: "loading",
                    label: (
                        <div className="w-full text-center py-2">
                            <span>{t("purchaseOrders:delivery_location.loading")}</span>
                        </div>
                    ),
                    disabled: true,
                },
            ]
        }

        if (error) {
            return [
                {
                    value: "error",
                    label: <div className="w-full text-center py-2 text-red-500">{t("error:error")}</div>,
                    disabled: true,
                },
            ]
        }

        if (!data || data.length === 0) {
            return []
        }

        return data.map(locAddToLocationForForm)
    }, [data, isLoading, error])

    // Handle input change with debouncing
    const debouncedSetSearchText = useDebouncedCallback(
        (value: string) => {
            if (isMountedRef.current) {
                setSearchText(value)
            }
        },
        DEBOUNCE_DURATION_MEDIUM,
        { maxWait: DEBOUNCE_DURATION_LONG },
    )

    const handleInputChange = useCallback(
        (value: string) => {
            debouncedSetSearchText.callback(value)
        },
        [debouncedSetSearchText],
    )

    // Optimized selection handler
    const handleSelection = useCallback(
        (_value: string, option: LocationForForm) => {
            if (option.value !== "loading" && option.value !== "error" && option.address) {
                onSelect(option.address)
            }
        },
        [onSelect, data, memoizedOptions],
    )

    // Cleanup on unmount
    useEffect(
        () => () => {
            isMountedRef.current = false
        },
        [],
    )

    // Memoized form item value getter
    const getFormItemValue = useCallback(
        (value: string, selected: { label: string; value: string }) => {
            if (!selected) {
                return value
            }

            const result = data?.find((item) => item.address === selected.value)

            if (!result) {
                return value
            }

            return {
                address: value,
                street: result.street,
                addressLine1: result.street,
                houseNumber: result.houseNumber,
                zip: result.zipCode,
                city: result.city,
                state: result.state,
                country: result.country,
                placeType: result.placeType,
                relevance: result.relevance,
            }
        },
        [data],
    )

    return (
        <Form.Item
            className={className ?? ""}
            label={label}
            name={name}
            key={formItemKey}
            rules={rules}
            {...rest}
            getValueFromEvent={getFormItemValue}
            getValueProps={(value) => {
                if (!value) {
                    return { value: "" }
                }
                if (typeof value === "string") {
                    return { value } // fallback
                }

                return { value: formatAddress(value) ?? value.address }
            }}
            validateTrigger="onBlur"
            initialValue={initialValue}
        >
            <AutoComplete
                placeholder={placeholder}
                onSearch={handleInputChange}
                options={memoizedOptions}
                onSelect={handleSelection}
                getPopupContainer={getPopupAnchor()}
                notFoundContent={
                    !isLoading ? <NoSearchDataFound imageStyle={{ height: 50, marginRight: "auto", marginLeft: "auto", marginTop: "unset", marginBottom: "unset" }} /> : null
                }
                filterOption={false} // Disable built-in filtering since we handle it server-side
                showSearch
                allowClear
            >
                <Input autoComplete="off" spellCheck={false} />
            </AutoComplete>
        </Form.Item>
    )
}
